import type React from "react";
import {
  type CtaClickProps,
  type ITrackingContext as ITeamschemeTrackingContext,
  TrackingContext as TeamschemeTrackingContext,
  type TrackEventProps,
  type TrackingValue,
} from "@naf/teamscheme";
import type {
  AccidentReportViewModel,
  VehicleKey,
} from "#accident-statement/schema";
import { normalizeComponentTree } from "../shared/search/normalizeComponentTree";
import { toUtmCampaign, type ReportLinkSource } from "./ReportLinkSource";
import { type ITrackingContext, TrackingContext } from "./TrackingContext";

export function AppTrackingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  function trackCtaClick({ navigate, label }: CtaClickProps) {
    const location = window.location;

    let destination: string | undefined = undefined;
    if (typeof navigate === "string") {
      destination = navigate;
    } else if (navigate) {
      destination = navigate.pathname;
    }

    let hostname: string | undefined = undefined;
    let absoluteDestinationPath: string | undefined = undefined;
    if (destination) {
      if (destination.startsWith("http")) {
        hostname = new URL(destination).hostname;
        absoluteDestinationPath = destination;
      } else if (!destination.startsWith("/")) {
        /*
          If destination does not start with the http-protocol we must assume we
          are on our own domain, so hostname of target must be origin of where we are.
        */
        hostname = location.origin;
        absoluteDestinationPath = `${hostname}/${destination}`;
      } else {
        hostname = location.origin;
        absoluteDestinationPath = hostname + destination;
      }
    }
    const eventName = "cta_click";
    const eventParams = {
      cta_location: location.pathname,
      cta_text: normalizeComponentTree(label),
      cta_hostname: location.origin,
      cta_url: absoluteDestinationPath,
    };

    window.dataLayer.push({
      event: eventName,
      event_params: eventParams,
    });

    console.log(eventName, {
      event_params: eventParams,
    });
  }
  function trackEvent({
    category,
    action,
    label,
    value,
    ...params
  }: TrackEventProps) {
    window.dataLayer.push({
      event: "event",
      category,
      action,
      label,
      value,
      ...params,
    });

    console.log("event", {
      category,
      action,
      label,
      value,
    });
  }

  function trackPageView({ title, path }: { title: string; path: string }) {
    window.dataLayer.push({
      event: "VirtualPageView",
      virtualPageURL: path,
      virtualPageTitle: title,
    });

    console.log("pageview", path, title);
  }

  function trackReportLoad(report: AccidentReportViewModel, party: VehicleKey) {
    const params = {
      id: report.id,
      party,
    };

    window.dataLayer.push({
      event: "report",
      ...params,
    });

    console.log("report", params);
  }

  function trackExperiment(experimentName: string) {
    window.dataLayer.push({
      event: "experiment",
      experimentName,
    });

    console.log("experiment", experimentName);
  }

  function trackAppLoad() {
    window.dataLayer.push({
      event: "load",
      appVersion: import.meta.env.PUBLIC_ENV__REACT_APP_VERSION,
    });
  }

  function addTrackingContext(contextObject: {
    [attribute: string]: TrackingValue;
  }) {
    for (const [attribute, value] of Object.entries(contextObject)) {
      window.dataLayer.push({ [attribute]: value });
    }
  }

  function trackReportLinkOpened(source: ReportLinkSource) {
    window.dataLayer.push({
      event: "contract_link_opened",
      campaign: toUtmCampaign(source),
    });
  }

  const trackingContext: ITeamschemeTrackingContext = {
    trackEvent,
    trackCtaClick,
  };
  const appTrackingContext: ITrackingContext = {
    event: trackEvent,
    pageView: trackPageView,
    appLoad: trackAppLoad,
    reportLoad: trackReportLoad,
    experiment: trackExperiment,
    context: addTrackingContext,
    reportLinkOpened: trackReportLinkOpened,
  };

  return (
    <TeamschemeTrackingContext.Provider value={trackingContext}>
      <TrackingContext.Provider value={appTrackingContext}>
        {children}
      </TrackingContext.Provider>
    </TeamschemeTrackingContext.Provider>
  );
}
